import {
  ContextMetadata,
  MutationWithContext,
  TypedContext,
} from '@lidea/shared/data-access/core';
import { Measures } from '@lidea/shared/util/types';
import { useMutationState } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useMeasures } from '../use-measures/use-measures';

const PENDING_SYNC_MUTATION_KEYS = new Set([
  'updateMeasure',
  'uploadCropPicture',
]);

function isPendingSyncMutation(mutation: MutationWithContext): boolean {
  const key = mutation.options.mutationKey?.[0];
  if (typeof key !== 'string' || !PENDING_SYNC_MUTATION_KEYS.has(key)) {
    return false;
  }

  if (mutation.state.status === 'success') {
    return false;
  }

  return (
    mutation.state.status === 'pending' || mutation.state.status === 'error'
  );
}

function getMeasureId(mutation: MutationWithContext): number | undefined {
  const key = mutation.options.mutationKey?.[0];
  const variables = mutation.state.variables;

  if (!variables || typeof variables !== 'object') {
    return undefined;
  }

  if (key === 'updateMeasure' && 'id' in variables) {
    return (variables as { id: number }).id;
  }

  if (key === 'uploadCropPicture' && 'measureId' in variables) {
    return (variables as { measureId: number }).measureId;
  }

  return undefined;
}

function resolvePointId(
  mutation: MutationWithContext,
  measures: Measures | undefined
): number | undefined {
  const metadata = (mutation.state.context as TypedContext)?.metadata as
    | ContextMetadata
    | undefined;

  if (metadata?.pointId != null) {
    return metadata.pointId;
  }

  const measureId = metadata?.measureId ?? getMeasureId(mutation);
  if (measureId == null || !measures) {
    return undefined;
  }

  return Object.values(measures).find((measure) => measure.id === measureId)
    ?.point.id;
}

export interface UsePendingSyncPointIdsProps {
  plotId: number;
  analysisId: number;
}

export function usePendingSyncPointIds(props: UsePendingSyncPointIdsProps) {
  const measures = useMeasures({ analysisId: props.analysisId });

  const pendingSyncMutations = useMutationState({
    filters: {
      mutationKey: [],
      predicate: (mutation) => {
        if (!isPendingSyncMutation(mutation as MutationWithContext)) {
          return false;
        }

        const metadata = (mutation.state.context as TypedContext)?.metadata as
          | ContextMetadata
          | undefined;

        return metadata?.fieldId === props.plotId;
      },
    },
    select: (mutation) => mutation as MutationWithContext,
  });

  return useMemo(() => {
    const pendingSyncPointIds = new Set<number>();
    const syncingPointIds = new Set<number>();

    for (const mutation of pendingSyncMutations) {
      const pointId = resolvePointId(mutation, measures.data);
      if (pointId == null) {
        continue;
      }

      pendingSyncPointIds.add(pointId);

      if (
        mutation.state.status === 'pending' &&
        !mutation.state.isPaused
      ) {
        syncingPointIds.add(pointId);
      }
    }

    return {
      pendingSyncPointIds: Array.from(pendingSyncPointIds),
      syncingPointIds: Array.from(syncingPointIds),
    };
  }, [measures.data, pendingSyncMutations]);
}

export default usePendingSyncPointIds;
